/**
 * Anuncios del tablero:
 * - Llamar paciente (TTS Edge vía API, nombre + consultorio)
 * - Consultorio libre (MP3 estáticos)
 */
import { API_URL } from '../config';

const VOZ_STORAGE = 'tablero-citas-voz';

type TrabajoAudio = { kind: 'url'; url: string } | { kind: 'file'; archivo: string };

let reproduciendo = false;
let player: HTMLAudioElement | null = null;
let unlockPlayer: HTMLAudioElement | null = null;
const cola: TrabajoAudio[] = [];
/** Claves ya anunciadas: llamar-123 / libre-123 */
const anunciados = new Set<string>();

export function vozHabilitada(): boolean {
  try {
    return localStorage.getItem(VOZ_STORAGE) !== '0';
  } catch {
    return true;
  }
}

export function setVozHabilitada(on: boolean): void {
  try {
    localStorage.setItem(VOZ_STORAGE, on ? '1' : '0');
  } catch {
    /* kiosk sin storage */
  }
}

export function hablaDisponible(): boolean {
  return typeof Audio !== 'undefined';
}

function urlEstatico(archivo: string): string {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${publicUrl}/audio/tablero/${archivo}`;
}

export function numeroConsultorio(raw: string | null | undefined): string | null {
  const t = String(raw || '').trim();
  if (!t) return null;
  const m = t.match(/(\d{1,2})/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (n < 1 || n > 20) return null;
  return String(n);
}

function archivoLibre(consultorio: string | null | undefined): string {
  const n = numeroConsultorio(consultorio);
  return n ? `libre_${n}.mp3` : 'libre_sin.mp3';
}

function urlAnuncioLlamar(paciente: string, consultorio: string | null | undefined): string {
  const q = new URLSearchParams();
  q.set('paciente', String(paciente || '').trim().slice(0, 60));
  const c = consultorio != null ? String(consultorio).trim() : '';
  if (c) q.set('consultorio', c.slice(0, 12));
  return `${API_URL}/public/tablero-anuncio?${q.toString()}`;
}

function obtenerPlayer(): HTMLAudioElement {
  if (!player) {
    player = new Audio();
    player.preload = 'auto';
    player.loop = false;
  }
  return player;
}

export function desbloquearVoz(): void {
  if (!hablaDisponible()) return;
  if (!unlockPlayer) unlockPlayer = new Audio();
  unlockPlayer.src = urlEstatico('silencio.wav');
  unlockPlayer.volume = 0.01;
  const p = unlockPlayer.play();
  if (p && typeof p.then === 'function') {
    p.then(() => {
      unlockPlayer?.pause();
      if (unlockPlayer) unlockPlayer.currentTime = 0;
    }).catch(() => {
      /* esperando gesto */
    });
  }
}

function reproducirSiguiente(): void {
  if (reproduciendo || !cola.length || !hablaDisponible() || !vozHabilitada()) return;
  const job = cola.shift()!;
  reproduciendo = true;
  const a = obtenerPlayer();
  try {
    a.pause();
    a.currentTime = 0;
  } catch {
    /* ignore */
  }
  a.loop = false;
  a.src = job.kind === 'url' ? job.url : urlEstatico(job.archivo);
  a.volume = 1;
  const fin = () => {
    reproduciendo = false;
    window.setTimeout(() => reproducirSiguiente(), 350);
  };
  a.onended = fin;
  a.onerror = fin;
  const p = a.play();
  if (p && typeof p.then === 'function') {
    p.catch(() => {
      desbloquearVoz();
      window.setTimeout(() => {
        void a.play().then(() => undefined).catch(fin);
      }, 150);
    });
  }
}

function encolar(job: TrabajoAudio): void {
  cola.push(job);
  reproducirSiguiente();
}

export function precargarVoces(): void {
  if (!hablaDisponible()) return;
  ['silencio.wav', 'libre_sin.mp3', 'libre_1.mp3'].forEach((f) => {
    const pre = new Audio();
    pre.preload = 'auto';
    pre.src = urlEstatico(f);
  });
  desbloquearVoz();
}

/** Cuando el médico toma la cita: «Ana García, pase al consultorio tres, por favor.» */
export function anunciarLlamarPaciente(
  paciente: string,
  consultorio: string | null | undefined,
  citaId: number
): void {
  if (!hablaDisponible() || !vozHabilitada()) return;
  const key = `llamar-${citaId}`;
  if (anunciados.has(key)) return;
  anunciados.add(key);
  // Evita cola infinita si llegan muchos eventos seguidos
  if (cola.length >= 8) return;
  encolar({ kind: 'url', url: urlAnuncioLlamar(paciente, consultorio) });
}

/** Cuando termina la consulta: consultorio libre (MP3 estático). */
export function anunciarConsultorioLibre(
  consultorio: string | null | undefined,
  citaId?: number
): void {
  if (!hablaDisponible() || !vozHabilitada()) return;
  if (citaId != null) {
    const key = `libre-${citaId}`;
    if (anunciados.has(key)) return;
    anunciados.add(key);
  }
  if (cola.length >= 8) return;
  encolar({ kind: 'file', archivo: archivoLibre(consultorio) });
}
